// ============================================================
// TERROR — ENDLESS DEPTHS
// Infinite ranked ladder inside the Inner Emperium.
// ELO-based, procedurally generated opponents, named
// gatekeepers at every tier transition.
// ============================================================

import { useState, useEffect, useRef } from "react";
import { SD } from "./theme";
import { ref, get, update } from "firebase/database";
import {
  addCardToCollection,
  GATEKEEPER_TROPHIES,
  STREAK_THRESHOLDS,
  eloTier,
  getNextGatekeeper,
  recordDepthResult as recordDepthResultV2,
} from "./progression";
import { pickDepthsOpponentName } from "./storyData";
import { setNextNpcBrain } from "./NPC";
import HoloFoil from "./HoloFoil";
import { db } from "./firebase";
import { useEquippedCompanion } from "./useEquippedCompanion";
import CompanionSprite from "./CompanionSprite";

// ── Grimoire Manuscript Palette ──────────────────────────────
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// The Depths' signature accent — the deep Solrend magenta already used
// throughout this screen as `accentColor` and the Solrend rank color.
// Named once here so the descent map and the home view share one value.
const SD_SOLREND = "#ff2266";

// ── Depth Rewards — card earned by defeating each gatekeeper ─
// CDN: https://terror-assets.pages.dev/{cardId}.webp
// holoEffect: the special variant applied to this copy
// ── Depth Rewards ──────────────────────────────────────────────
// Derived view over progression.GATEKEEPER_TROPHIES, shaped for
// the UI that renders "which gatekeeper drops what."
// All trophies use the unified holo_depths foil — ocean blue,
// slow breathing pulse — so the Depths mode has one visual
// signature across every card it drops (trophies, streak drops,
// inner circle). The gatekeeper identity is carried by the
// trophy CARD itself, not the foil.
// ═══════════════════════════════════════════════════════════════
const GATEKEEPER_ID_MAP = {
  gk_suhnker: "suhnker",
  gk_zhefir:  "zhefir",
  gk_tyrent:  "tyrent",
  gk_vael:    "vael",
  gk_ruk:     "ruk",
};

// Human-readable name for each trophy card
const TROPHY_CARD_NAMES = {
  premier_dragon_beb:   "Premier Dragon Beb",
  spectral_tiger_lord:  "Spectral Tiger Lord",
  h01_lord:             "H-01 Super Nova",
  xulan_lord:           "Xulan-Shadow",
  chaos_dragon_lord:    "Chaos Dragon Lord",
};

export const DEPTH_REWARDS = [
  { legacyId: "gk_suhnker", gk: GATEKEEPER_TROPHIES.suhnker, rankName: "Beb",        rankColor: "#16a34a" },
  { legacyId: "gk_zhefir",  gk: GATEKEEPER_TROPHIES.zhefir,  rankName: "Champion",   rankColor: "#3b82f6" },
  { legacyId: "gk_tyrent",  gk: GATEKEEPER_TROPHIES.tyrent,  rankName: "Lord",       rankColor: "#7c3aed" },
  { legacyId: "gk_vael",    gk: GATEKEEPER_TROPHIES.vael,    rankName: "Escendant",  rankColor: "#dc2626" },
  { legacyId: "gk_ruk",     gk: GATEKEEPER_TROPHIES.ruk,     rankName: "Aenarchist", rankColor: "#d4a84b" },
].map(({ legacyId, gk, rankName, rankColor }) => ({
  gatekeeperId: legacyId,
  gkKey:        gk.id,
  rankName,
  rankColor,
  cardId:       gk.trophyCardId,
  cardName:     TROPHY_CARD_NAMES[gk.trophyCardId] || gk.trophyCardId,
  holoEffect:   "holo_depths",        // unified Depths foil for ALL trophies
  holoLabel:    "◆ Depths Edition",
  holoColor:    "#00aaff",            // deep ocean blue
  requiredElo:  gk.eloFloor,
  flavor:       gk.trophyNote,
  gatekeeperName: gk.name,
  gatekeeperTitle: gk.title,
}));


// ─────────────────────────────────────────────────────────────
// RANK LADDER DEFINITION
// ─────────────────────────────────────────────────────────────
export const RANKS = [
  {
    id: "escling",
    name: "Escling",
    color: "#888888",
    minElo: 0,
    maxElo: 399,
    divisions: 5,
    difficulty: "Beb",
    flavor: "The depths open before you. Few return unchanged.",
  },
  {
    id: "beb",
    name: "Beb",
    color: "#16a34a",
    minElo: 400,
    maxElo: 799,
    divisions: 4,
    difficulty: "Beb",
    flavor: "You have survived the first descent. The depths noticed.",
  },
  {
    id: "champion",
    name: "Champion",
    color: "#3b82f6",
    minElo: 800,
    maxElo: 1199,
    divisions: 4,
    difficulty: "Champion",
    flavor: "The beings here remember what it felt like to win.",
  },
  {
    id: "lord",
    name: "Lord",
    color: "#7c3aed",
    minElo: 1200,
    maxElo: 1699,
    divisions: 4,
    difficulty: "Lord",
    flavor: "Every opponent you face here has ruined hundreds before you.",
  },
  {
    id: "escendant",
    name: "Escendant",
    color: "#dc2626",
    minElo: 1700,
    maxElo: 2199,
    divisions: 3,
    difficulty: "Lord",
    flavor: "You are no longer climbing. You are ascending.",
  },
  {
    id: "aenarchist",
    name: "Aenarchist",
    color: "#d4a84b",
    minElo: 2200,
    maxElo: 2799,
    divisions: 3,
    difficulty: "Lord",
    flavor: "The Depths have accepted you as one of their own.",
  },
  {
    id: "solrend",
    name: "Solrend",
    color: "#ff2266",
    minElo: 2800,
    maxElo: Infinity,
    divisions: 0, // truly infinite — no ceiling
    difficulty: "Lord",
    flavor: "There is no top. There is only deeper.",
  },
];

// ─────────────────────────────────────────────────────────────
// GATEKEEPERS — named bosses at tier transition points
// Must be defeated before ELO can push you to the next tier
// ─────────────────────────────────────────────────────────────
export const GATEKEEPERS = [
  {
    id: "gk_suhnker",
    name: "S'uhnker",
    title: "Sentinel of the Outer Gate",
    avatarIcon: "⚔",
    accentColor: "#3b82f6",
    requiredElo: 400,      // unlocks after reaching Beb ELO
    grantsEntry: "beb",    // blocks entry to this tier
    difficulty: "Champion",
    starterDeckId: "starter_h09",
    // DEPTHS BRAIN (authored): the wall at the outer gate — it holds, it waits, it never overreaches
    npcSkill: 0.45,
    temperament: "Patient",
    dialogue: {
      approach: "◆ The descent ends here. Prove otherwise.",
      onWin: "◆ ...Pass. The deeper gate will not be so forgiving.",
      onLoss: "◆ The gate holds. As it always has.",
    },
  },
  {
    id: "gk_zhefir",
    name: "Z'hefir",
    title: "Keeper of Shadows",
    avatarIcon: "◇",
    accentColor: "#6366f1",
    requiredElo: 800,
    grantsEntry: "champion",
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    // DEPTHS BRAIN (authored): your shadow told it everything — it does not roll dice, it already knows
    npcSkill: 0.55,
    temperament: "Cold",
    dialogue: {
      approach: "◆ Your shadow arrived before you did. It told me everything.",
      onWin: "◆ Hmm. Your shadow fights harder than you do. Interesting.",
      onLoss: "◆ Even your shadow retreats now. Do the same.",
    },
  },
  {
    id: "gk_tyrent",
    name: "T'yrent",
    title: "The Last Flame",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    requiredElo: 1200,
    grantsEntry: "lord",
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    // DEPTHS BRAIN (authored): the Last Flame burns forward — prophecy brain lit, throttle open
    npcSkill: 0.72,
    temperament: "Reckless",
    dialogue: {
      approach: "◆ I was beginning to think no one worthy would ever arrive.",
      onWin: "◆ The Depths have found their champion. For now.",
      onLoss: "◆ Even fire has its hierarchy, Aenarchist.",
    },
  },
  {
    id: "gk_vael",
    name: "V'ael",
    title: "The One Who Was Left",
    avatarIcon: "◆",
    accentColor: "#7c3aed",
    requiredElo: 1700,
    grantsEntry: "escendant",
    difficulty: "Champion",
    starterDeckId: "starter_shadow_fox",
    // DEPTHS BRAIN (authored): a Champion playing at genius level — line-tutored, triple-souled, deterministic
    npcSkill: 0.85,
    temperament: "Cold",
    dialogue: {
      approach: "◆ You opened the door. I have been waiting on the other side.",
      onWin: "◆ There is something in you that was not there before. I see it now.",
      onLoss: "◆ The door closes. It will open again. It always does.",
    },
  },
  {
    id: "gk_ruk",
    name: "Rük",
    title: "Fracture Made Flesh",
    avatarIcon: "◈",
    accentColor: "#dc2626",
    requiredElo: 2200,
    grantsEntry: "aenarchist",
    difficulty: "Lord",
    starterDeckId: "starter_chaos_dragon",
    // DEPTHS BRAIN (authored): Fracture Made Flesh — denial + lethal focus + a bottomless Primera hunger
    npcSkill: 0.95,
    temperament: "Greedy",
    dialogue: {
      approach: "◆ You. Finally. I have been ruining lesser Aenarchists for too long.",
      onWin: "◆ ...Acceptable. The Depths will remember your name.",
      onLoss: "◆ Rük does not lose. Rük merely postpones.",
    },
  },
];

// ─────────────────────────────────────────────────────────────
// PROCEDURAL OPPONENT GENERATOR
// ─────────────────────────────────────────────────────────────
const ALL_DECK_IDS = [
  "starter_h01", "starter_h09", "starter_xulan",
  "starter_spectral_tiger", "starter_shadow_fox", "starter_inverted_serpent",
  "starter_z4z", "starter_dg0", "starter_antdroid",
  "starter_lucky_dragon", "starter_premier_dragon", "starter_chaos_dragon",
];

function pickRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// ─────────────────────────────────────────────────────────────
// DEPTHS BRAIN — ELO → skill, plus a rolled temperament
// ─────────────────────────────────────────────────────────────
// skill is a 0..1 DECISION-QUALITY scalar consumed by NPC.js. It never
// touches stats or rules — a deeper opponent simply plays better.
// 2800 (the Solrend floor) maps to 1.0; everything past it stays 1.0 —
// "There is no top. There is only deeper" is flavor, the ceiling of
// perfect play is real. Landmarks on the ladder (NPC.js gates):
//   ~1064  Champions start tutoring their ascension lines (0.38)
//   ~1960  Lords wake their PROPHECY brain (0.70)
//   ~2296  Lords gain LETHAL FOCUS (0.82)
//    2660+ Lords gain PROPHECY DENIAL (0.95) — Solrend's doorstep
export function skillFromElo(elo) {
  return Math.max(0, Math.min(1, (Number(elo) || 0) / 2800));
}

// Every procedural opponent rolls a hidden temperament — same
// rulebook, different soul. Two 900-ELO Champions no longer play the
// same duel. (Profiles live in NPC.js TEMPERAMENT_PROFILES.)
const DEPTH_TEMPERAMENTS = ["Reckless", "Patient", "Greedy", "Cold"];

// Procedural opponent — uses the Krush-style name pool from storyData
// (real words cryptically spelled, e.g. N'vise = Novice, T'mpest = Tempest).
// Enemies do NOT get titles — titles are earned rewards for players only.
export function generateOpponent(elo, difficulty) {
  const picked = pickDepthsOpponentName(elo);  // { name, hidden, nameless? }
  const deck   = pickRandom(ALL_DECK_IDS);

  return {
    id: `proc_${Date.now()}_${Math.floor(Math.random()*9999)}`,
    name: picked.name,
    // No `title` field — enemies are just named, full stop.
    // `hidden` kept internally for any future "reveal the word" reactions;
    // never shown in normal play.
    hidden:   picked.hidden || null,
    nameless: !!picked.nameless,
    isProc:   true,
    difficulty,
    // DEPTHS BRAIN — consumed by handleFight → setNextNpcBrain
    npcSkill:    skillFromElo(elo),
    temperament: pickRandom(DEPTH_TEMPERAMENTS),
    starterDeckId: deck,
    accentColor: "#ff2266",
    avatarIcon: picked.nameless ? "—" : "◆",
    dialogue: {
      approach: generateApproach(difficulty),
    },
  };
}

function generateApproach(difficulty) {
  const lines = {
    Beb: [
      "◆ Another one descends.",
      "◆ The Depths sent you. Interesting.",
      "◆ You smell like the surface still.",
      "◆ I have been waiting for a challenge.",
    ],
    Champion: [
      "◆ I have studied every deck in the Depths. I know yours already.",
      "◆ Your Escendants are strong. They will not be enough.",
      "◆ The fracture comes for us all. Yours comes today.",
      "◆ I have ruined better Aenarchists than you.",
    ],
    Lord: [
      "◆ I have not lost in this Depth since before you knew this game existed.",
      "◆ Every being I have ever faced became part of my necropolis.",
      "◆ Your Anima will dim. This is certain. Only the timing is in question.",
      "◆ I have waited an eternity for an opponent worthy of my final form.",
      "◆ The Lord of Lords taught me everything. You are about to learn everything too.",
    ],
  };
  return pickRandom(lines[difficulty] || lines.Beb);
}

// ─────────────────────────────────────────────────────────────
// ELO CALCULATION
// ─────────────────────────────────────────────────────────────
export const ELO_CONFIG = {
  BASE_WIN:    28,   // base ELO gained on win
  BASE_LOSS:   18,   // base ELO lost on loss
  DRAW:         5,   // ELO lost on draw
  // Difficulty multipliers
  DIFF_MULT: { Beb: 0.8, Champion: 1.0, Lord: 1.3 },
  // Can never drop below your current tier's floor (unless you lose to gatekeeper)
  FLOOR_PROTECTION: true,
};

export function getRankInfo(elo) {
  const rank = [...RANKS].reverse().find(r => elo >= r.minElo) || RANKS[0];
  if (rank.id === "solrend") {
    // Solrend has no ceiling — calculate virtual division
    const above = elo - rank.minElo;
    const division = Math.floor(above / 200) + 1;
    const progress = (above % 200) / 200;
    return { rank, division, progress, label: `Solrend ${division}` };
  }
  const span = rank.maxElo - rank.minElo + 1;
  const divSpan = span / rank.divisions;
  const above = elo - rank.minElo;
  const division = Math.min(rank.divisions, Math.floor(above / divSpan) + 1);
  const divProgress = (above % divSpan) / divSpan;
  return {
    rank,
    division,
    progress: divProgress,
    label: `${rank.name} ${toRoman(division)}`,
  };
}

function toRoman(n) {
  const map = { 1:"I", 2:"II", 3:"III", 4:"IV", 5:"V" };
  return map[n] || `${n}`;
}

export function calcEloChange(currentElo, opponentDifficulty, result) {
  const mult = ELO_CONFIG.DIFF_MULT[opponentDifficulty] || 1;
  if (result === "win")  return Math.round(ELO_CONFIG.BASE_WIN  * mult);
  if (result === "loss") return -Math.round(ELO_CONFIG.BASE_LOSS * mult);
  if (result === "draw") return -ELO_CONFIG.DRAW;
  return 0;
}

// ─────────────────────────────────────────────────────────────
// FIREBASE — ELO & DEPTH PROGRESS
// ─────────────────────────────────────────────────────────────
export async function loadDepthProfile(uid) {
  const snap = await get(ref(db, `profiles/${uid}/endlessDepths`));
  return snap.exists() ? snap.val() : {
    elo: 0,
    peakElo: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    streak: 0,
    bestStreak: 0,
    defeatedGatekeepers: [],
    history: [],
  };
}

// Adapter — delegates to progression.recordDepthResult (the V2 engine
// that processes three reward layers: Gatekeeper Trophies, Streak
// Drops, Inner Circle). Renamed from `recordDepthResult` → 
// `recordDepthResultLegacy` (July): there used to be TWO exports named
// `recordDepthResult` — this 4-arg adapter and the 6-arg V2 engine in
// progression.js. A caller importing the wrong one got its args
// silently mis-mapped (an opponent object landing where opponentName
// wanted a string, etc.) with no crash, just wrong rewards. Distinct
// name = the collision is now structurally impossible.
//
// Legacy signature: recordDepthResultLegacy(uid, result, opponent, eloChange)
//   `opponent` is the OBJECT ({id,name,isGatekeeper,difficulty}); this
//   adapter unpacks it for V2. Returns { newElo, newPeak, eloChange,
//   rewards } — rewards carries any trophy card / streak drop / title
//   this result granted (Inner Circle ctx plumbing removed July 2026).
export async function recordDepthResultLegacy(uid, result, opponent, eloChange, ctx = {}) {
  // The V2 function computes eloChange internally from opponent difficulty,
  // but legacy calls pre-compute it via calcEloChange and pass it in.
  // To preserve legacy behavior, we override the V2's computed delta by
  // pre-writing the current elo shifted so V2 lands on the same newElo.
  // Simpler: fetch current, apply legacy eloChange, call V2 with difficulty
  // so it computes the same delta. Since V2 already uses DIFF_MULT and
  // BASE_WIN/LOSS, and our calcEloChange below uses the same ELO_CONFIG,
  // the numbers match when opponent.difficulty is passed through.
  const isGatekeeper = !!opponent.isGatekeeper;
  const gatekeeperId = isGatekeeper
    ? (GATEKEEPER_ID_MAP[opponent.id] || opponent.id)
    : null;

  return await recordDepthResultV2(
    uid,
    result,
    opponent.name,
    isGatekeeper,
    opponent.difficulty,
    { gatekeeperId }
  );
}

// ─────────────────────────────────────────────────────────────
// RANK CARD COMPONENT
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// DEPTH REWARDS SECTION
// Shows on the home screen — what cards you earn by climbing
// ─────────────────────────────────────────────────────────────
function DepthRewardsSection({ depth }) {
  const [expanded, setExpanded] = useState(false);
  const defeatedGk = depth?.defeatedGatekeepers || [];
  const elo = depth?.elo || 0;
  const streak = depth?.streak || 0;
  const bestStreak = depth?.bestStreak || 0;
  const streakHits = depth?.streakMilestonesHit || [];

  // Check a reward as "defeated" against both legacy (gk_suhnker)
  // and short (suhnker) id formats — handles profiles written by
  // either system without data migration.
  const isDefeated = (reward) =>
    defeatedGk.includes(reward.gatekeeperId) ||
    defeatedGk.includes(reward.gkKey);

  // Which reward is most recently unlocked (the "featured" one)
  const unlockedRewards = DEPTH_REWARDS.filter(isDefeated);
  const nextReward = DEPTH_REWARDS.find(r => !isDefeated(r));
  const featuredReward = unlockedRewards[unlockedRewards.length - 1] || null;
  const displayReward = featuredReward || nextReward;

  // Streak panel data
  const nextStreakMilestone = STREAK_THRESHOLDS.find(t => t.at > streak);


  return (
    <div style={{
      background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`,
      border: `1px solid ${GM.inkBrown}`,
      borderRadius: 4, marginBottom: 14, overflow: "hidden",
    }}>
      {/* Collapsed header — always visible */}
      <button onClick={() => setExpanded(v => !v)} style={{
        width: "100%", display: "flex", alignItems: "center",
        justifyContent: "space-between",
        background: "transparent", border: "none", cursor: "pointer",
        padding: "12px 16px", fontFamily: GM.font,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14, color: "#ff2266" }}>◆</span>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: 10, fontWeight: 900, color: GM.inkGoldBright, letterSpacing: 2 }}>
              DEPTH REWARDS
            </div>
            <div style={{ fontSize: 9, color: GM.vellumFaint, marginTop: 1 }}>
              {unlockedRewards.length}/{DEPTH_REWARDS.length} earned · defeat gatekeepers to collect
            </div>
          </div>
        </div>
        <span style={{ color: GM.vellumFaint, fontSize: 11, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: "0 16px 16px" }}>

          {/* Divider */}
          <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${GM.inkBrown}55, transparent)`, marginBottom: 14 }} />

          {/* Featured card — the next unlock or most recent earn */}
          {displayReward && (() => {
            const earned = isDefeated(displayReward);
            return (
            <div style={{ display: "flex", gap: 14, marginBottom: 16, alignItems: "flex-start" }}>

              {/* Card image — canonical CDN URL per build guide.
                  Pure-dark fallback background + visible art-pending state
                  when the CDN file isn't deployed yet (build guide says
                  placeholders are expected during development). */}
              <div style={{
                width: 80, flexShrink: 0,
                aspectRatio: "2/3", borderRadius: 6, overflow: "hidden",
                border: `1px solid ${earned ? displayReward.holoColor : GM.pageBorder}`,
                position: "relative",
                boxShadow: earned ? `0 0 16px ${displayReward.holoColor}55` : "none",
                opacity: earned ? 1 : 0.45,
                background: GM.page,
              }}>
                <img
                  key={displayReward.cardId}
                  src={`https://terror-assets.pages.dev/${displayReward.cardId}.webp`}
                  alt={displayReward.cardName}
                  onError={e => {
                    e.target.style.display = "none";
                    // Show the sibling "art pending" placeholder
                    const sib = e.target.nextElementSibling;
                    if (sib) sib.style.display = "flex";
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                {/* Art-pending placeholder — hidden by default, revealed via onError.
                    Clear, readable, not a mystery rectangle. */}
                <div style={{
                  position: "absolute", inset: 0,
                  display: "none",
                  flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 4, background: GM.page, color: GM.vellumFaint,
                  fontFamily: GM.font, padding: 6, textAlign: "center",
                }}>
                  <div style={{ fontSize: 18, color: displayReward.holoColor, opacity: 0.6 }}>◆</div>
                  <div style={{ fontSize: 7, letterSpacing: 1.5, color: GM.vellumFaint }}>
                    ART PENDING
                  </div>
                </div>
                {/* THE ONE TRUE FOIL — canonical holo_depths (caustics).
                    Rendered by HoloFoil so the preview is ALWAYS exactly
                    what drops into the collection. Dimmed until earned. */}
                <HoloFoil tier="holo_depths" intensity={earned ? 1 : 0.5} radius={6} />
                {/* Lock overlay — dims unearned cards. No giant ◆ glyph
                    because the sigil overlays would amplify it into a
                    visible gold diamond (the bug we chased for 5 turns). */}
                {!earned && (
                  <div style={{
                    position: "absolute", inset: 0,
                    background: "rgba(2,1,0,0.55)",
                    pointerEvents: "none",
                    zIndex: 4,
                  }} />
                )}
                {/* Holo badge */}
                {earned && (
                  <div style={{
                    position: "absolute", bottom: 4, left: 0, right: 0,
                    textAlign: "center",
                    fontSize: 7, color: displayReward.holoColor,
                    fontFamily: GM.font, fontWeight: 900, letterSpacing: 1,
                    textShadow: `0 0 6px ${displayReward.holoColor}`,
                  }}>{displayReward.holoLabel}</div>
                )}
              </div>

              {/* Card info */}
              <div style={{ flex: 1, paddingTop: 2 }}>
                <div style={{ fontSize: 8, color: displayReward.rankColor, letterSpacing: 3, fontWeight: 900, fontFamily: GM.font, marginBottom: 4 }}>
                  {earned ? "✦ EARNED" : "◆ NEXT REWARD"}
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, color: earned ? GM.vellum : GM.vellumFaint, fontFamily: GM.font, marginBottom: 4 }}>
                  {displayReward.cardName}
                </div>
                <div style={{ fontSize: 9, color: displayReward.holoColor, fontWeight: 900, letterSpacing: 1, fontFamily: GM.font, marginBottom: 6 }}>
                  {displayReward.holoLabel}
                </div>
                <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", lineHeight: 1.6, fontFamily: GM.font }}>
                  {earned
                    ? displayReward.flavor
                    : `Defeat ${displayReward.gatekeeperName} — ${displayReward.gatekeeperTitle} — to unlock.`}
                </div>
                {!earned && (
                  <div style={{ marginTop: 6, fontSize: 9, color: GM.inkGoldDim, fontFamily: GM.font }}>
                    Reach {displayReward.requiredElo} ELO to challenge.
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* ── STREAK PANEL ─────────────────────────────────── */}
          <div style={{
            background: `${GM.pageMid}88`, border: `1px solid ${GM.inkBrown}44`,
            borderRadius: 4, padding: "10px 14px", marginBottom: 12,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div style={{ fontSize: 8, color: "#00aaff", letterSpacing: 3, fontWeight: 900, fontFamily: GM.font }}>
                ◆ CURRENT STREAK
              </div>
              <div style={{ fontSize: 8, color: GM.vellumFaint, fontFamily: GM.font }}>
                best: {bestStreak}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: streak > 0 ? "#00aaff" : GM.vellumFaint, fontFamily: GM.font, textShadow: streak >= 3 ? "0 0 12px #00aaff88" : "none" }}>
                {streak}
              </div>
              <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic", fontFamily: GM.font, flex: 1 }}>
                {nextStreakMilestone
                  ? `${nextStreakMilestone.at - streak} more win${nextStreakMilestone.at - streak === 1 ? "" : "s"} → ${nextStreakMilestone.pool === "title" ? "a title" : `a ${nextStreakMilestone.pool}-tier drop`}`
                  : "All streak rewards claimed."}
              </div>
            </div>
            {/* Milestone pips */}
            <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
              {STREAK_THRESHOLDS.map(t => {
                const hit = streakHits.includes(`s${t.at}`);
                return (
                  <div key={t.at} style={{
                    flex: 1, height: 4, borderRadius: 2,
                    background: hit ? "#00aaff" : `${GM.inkBrown}33`,
                    boxShadow: hit ? "0 0 4px #00aaff88" : "none",
                  }} title={`${t.at} wins — ${t.note}`} />
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 3, fontSize: 7, color: GM.vellumFaint, letterSpacing: 1 }}>
              {STREAK_THRESHOLDS.map(t => (
                <div key={t.at} style={{ flex: 1, textAlign: "center", fontFamily: GM.font }}>{t.at}</div>
              ))}
            </div>
          </div>


          {/* All rewards — compact row */}
          <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 3, fontFamily: GM.font, marginBottom: 8 }}>ALL REWARDS</div>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {DEPTH_REWARDS.map((r, i) => {
              const earned = defeatedGk.includes(r.gatekeeperId);
              return (
                <div key={r.gatekeeperId} style={{
                  flexShrink: 0, width: 52,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                }}>
                  {/* Mini card */}
                  <div style={{
                    width: 52, aspectRatio: "2/3", borderRadius: 4, overflow: "hidden",
                    border: `1px solid ${earned ? r.holoColor : GM.pageBorder}`,
                    position: "relative", opacity: earned ? 1 : 0.35,
                    boxShadow: earned ? `0 0 8px ${r.holoColor}44` : "none",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    // clicking a reward tile makes it the featured card
                    // by just cycling — simplest approach, no extra state
                  }}>
                    <img
                      src={`https://terror-assets.pages.dev/${r.cardId}.webp`}
                      alt={r.cardName}
                      onError={e => { e.target.style.display = "none"; }}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {!earned && (
                      <div style={{
                        position: "absolute", inset: 0,
                        background: "rgba(2,1,0,0.6)",
                        pointerEvents: "none",
                      }} />
                    )}
                    {earned && (
                      <HoloFoil tier="holo_depths" intensity={0.85} radius={4} />
                    )}
                  </div>
                  {/* Rank label */}
                  <div style={{ fontSize: 7, color: earned ? r.rankColor : GM.pageBorder, fontFamily: GM.font, fontWeight: 900, letterSpacing: 1, textAlign: "center" }}>
                    {r.rankName.toUpperCase()}
                  </div>
                  {earned && (
                    <div style={{ fontSize: 6, color: r.holoColor, fontFamily: GM.font, letterSpacing: 0.5, textAlign: "center" }}>✦</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function RankCard({ elo, depth }) {
  const { rank, division, progress, label } = getRankInfo(elo);
  const isGold = elo >= 2800;
  const barW = Math.round(progress * 100);

  return (
    <div style={{ background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.pageWarm})`, border: `1px solid ${rank.color}44`,
      borderRadius: 4, padding: "16px 20px", marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, color: rank.color, letterSpacing: 4,
            fontFamily: GM.font, marginBottom: 4 }}>
            ◆ ENDLESS DEPTHS
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: GM.vellum,
            fontFamily: GM.font, textShadow: `0 0 20px ${rank.color}` }}>
            {label}
          </div>
          {rank.id !== "solrend" && (
            <div style={{ fontSize: 9, color: "#555", letterSpacing: 2, marginTop: 2 }}>
              {elo} / {rank.maxElo} ELO
            </div>
          )}
          {rank.id === "solrend" && (
            <div style={{ fontSize: 9, color: rank.color, letterSpacing: 2, marginTop: 2 }}>
              {elo} ELO — NO CEILING ◆ INFINITE
            </div>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, marginBottom: 4, fontFamily: GM.font }}>PEAK</div>
          <div style={{ fontSize: 14, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.font }}>{depth?.peakElo || 0}</div>
        </div>
      </div>

      {/* Progress bar */}
      {rank.id !== "solrend" && (
        <div style={{ background: GM.pageBorder, borderRadius: 0, height: 3, overflow: "hidden" }}>
          <div style={{ width: `${barW}%`, height: "100%", background: rank.color,
            borderRadius: 4, transition: "width 0.6s ease",
            boxShadow: `0 0 8px ${rank.color}` }} />
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: "flex", gap: 20, marginTop: 12 }}>
        {[
          { label: "W", val: depth?.wins || 0, color: "#22c55e" },
          { label: "L", val: depth?.losses || 0, color: "#dc2626" },
          { label: "STREAK", val: depth?.streak || 0, color: GM.inkGold },
          { label: "BEST", val: depth?.bestStreak || 0, color: GM.inkGoldBright },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.font }}>{s.label}</div>
            <div style={{ fontSize: 14, fontWeight: 900, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {rank.flavor && (
        <div style={{ fontSize: 10, color: GM.vellumFaint, fontStyle: "italic",
          marginTop: 10, borderTop: `1px solid ${GM.ruling}`,
          paddingTop: 8, lineHeight: 1.6 }}>
          "{rank.flavor}"
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// GATEKEEPER CHALLENGE SCREEN
// ─────────────────────────────────────────────────────────────
function GatekeeperChallenge({ gatekeeper, onFight, onBack }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!ready) return;
      if (e.key === "Enter" || e.key === "q" || e.key === "Q") { e.preventDefault(); onFight(); }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ready, onFight, onBack]);

  const c = gatekeeper.accentColor;

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ fontSize: 9, color: c, letterSpacing: 4,
        fontFamily: GM.font, marginBottom: 8, textAlign: "center" }}>
        ⚔ TIER GATEKEEPER
      </div>
      <div style={{ fontSize: 11, color: "#555", letterSpacing: 2,
        fontFamily: GM.font, marginBottom: 20, textAlign: "center" }}>
        DEFEAT TO ADVANCE
      </div>

      <div style={{ background: `${c}10`, border: `1px solid ${c}44`,
        borderRadius: 14, padding: "20px", marginBottom: 20, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>{gatekeeper.avatarIcon}</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: c,
          fontFamily: GM.font, marginBottom: 4,
          textShadow: `0 0 16px ${c}` }}>
          {gatekeeper.name}
        </div>
        <div style={{ fontSize: 10, color: "#555", letterSpacing: 3,
          marginBottom: 16 }}>{gatekeeper.title}</div>
        <div style={{ fontSize: 12, color: "#888", fontStyle: "italic",
          lineHeight: 1.8, marginBottom: 16 }}>
          "{gatekeeper.dialogue.approach}"
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 8,
          background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <span style={{ fontSize: 14 }}>
            {gatekeeper.difficulty === "Lord" ? "👑" : "⚔"}
          </span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 900, color: c,
              fontFamily: GM.font }}>{gatekeeper.difficulty}</div>
            <div style={{ fontSize: 9, color: "#555" }}>Fixed difficulty — no mercy</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 10, color: "#444", textAlign: "center",
        marginBottom: 16, lineHeight: 1.7 }}>
        You cannot advance past this rank until this being is defeated.<br />
        You may still fight procedural opponents to maintain ELO.
      </div>

      <button onClick={onFight}
        style={{ width: "100%", padding: "14px", borderRadius: 10,
          background: `${c}15`, border: `1px solid ${c}`,
          color: c, fontFamily: GM.font, fontSize: 14,
          fontWeight: 900, cursor: "pointer", marginBottom: 10,
          textShadow: `0 0 8px ${c}`,
          animation: "fightPulse 1.4s ease-in-out infinite alternate" }}>
        ⚔ Challenge {gatekeeper.name}
      </button>
      <button onClick={onBack}
        style={{ width: "100%", padding: "10px", borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.08)", background: "transparent",
          color: "#555", fontSize: 12, cursor: "pointer",
          fontFamily: GM.font }}>
        ← Back
      </button>
      <style>{`@keyframes fightPulse { from{opacity:.8;transform:scale(1)} to{opacity:1;transform:scale(1.01)} }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// OPPONENT PREVIEW
// ─────────────────────────────────────────────────────────────
function OpponentPreview({ opponent, eloChange, onFight, onReroll, onBack }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 400);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (!ready) return;
      if (e.key === "Enter" || e.key === "q" || e.key === "Q") { e.preventDefault(); onFight(); }
      if (e.key === "Escape" || e.key === "Backspace") { e.preventDefault(); onBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ready, onFight, onBack]);

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ fontSize: 9, color: "#ff2266", letterSpacing: 4,
        fontFamily: GM.font, marginBottom: 16, textAlign: "center" }}>
        ◆ NEXT OPPONENT
      </div>

      <div style={{ background: "rgba(255,34,102,0.06)", border: "1px solid rgba(255,34,102,0.2)",
        borderRadius: 14, padding: "20px", marginBottom: 20, textAlign: "center" }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>◆</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#fff",
          fontFamily: GM.font, marginBottom: 4 }}>{opponent.name}</div>
        <div style={{ fontSize: 10, color: "#555", letterSpacing: 3,
          marginBottom: 16 }}>
          {opponent.title /* named gatekeeper */
            ? opponent.title
            : `◆ ${opponent.difficulty?.toUpperCase() || "TIER"} ◆` /* procedural — no title, show tier */}
        </div>
        <div style={{ fontSize: 12, color: "#666", fontStyle: "italic",
          lineHeight: 1.8, marginBottom: 16 }}>
          "{opponent.dialogue.approach}"
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#444", letterSpacing: 2, marginBottom: 2 }}>DIFFICULTY</div>
            <div style={{ fontSize: 12, fontWeight: 900,
              color: opponent.difficulty === "Lord" ? "#dc2626" :
                     opponent.difficulty === "Champion" ? GM.inkGold : "#a78bfa",
              fontFamily: GM.font }}>
              {opponent.difficulty}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#444", letterSpacing: 2, marginBottom: 2 }}>WIN</div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#22c55e" }}>+{eloChange} ELO</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 9, color: "#444", letterSpacing: 2, marginBottom: 2 }}>LOSS</div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "#dc2626" }}>
              -{Math.round(ELO_CONFIG.BASE_LOSS * (ELO_CONFIG.DIFF_MULT[opponent.difficulty] || 1))} ELO
            </div>
          </div>
        </div>
      </div>

      <button onClick={onFight}
        style={{ width: "100%", padding: "14px", borderRadius: 10,
          background: "rgba(255,34,102,0.1)", border: "1px solid #ff2266",
          color: "#ff2266", fontFamily: GM.font, fontSize: 14,
          fontWeight: 900, cursor: "pointer", marginBottom: 10 }}>
        ◆ Descend
      </button>
      <button onClick={onReroll}
        style={{ width: "100%", padding: "10px", borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.08)", background: "transparent",
          color: "#555", fontSize: 12, cursor: "pointer",
          fontFamily: GM.font, marginBottom: 8 }}>
        ↺ Different opponent
      </button>
      <button onClick={onBack}
        style={{ width: "100%", padding: "10px", borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.08)", background: "transparent",
          color: "#444", fontSize: 12, cursor: "pointer",
          fontFamily: GM.font }}>
        ← Back
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RANK HISTORY
// ─────────────────────────────────────────────────────────────
function DepthHistory({ history }) {
  if (!history?.length) return (
    <div style={{ textAlign: "center", color: "#444", fontSize: 12,
      fontStyle: "italic", padding: "20px 0" }}>
      No battles recorded yet.
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {history.slice(0, 10).map((h, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", borderRadius: 8,
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 10, fontWeight: 900, width: 24, textAlign: "center",
            color: h.result === "win" ? "#22c55e" : h.result === "loss" ? "#dc2626" : "#888" }}>
            {h.result === "win" ? "W" : h.result === "loss" ? "L" : "D"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "#888" }}>
              {h.opponentName}
              {h.isGatekeeper && <span style={{ color: GM.inkGold, marginLeft: 6 }}>⚔</span>}
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 900,
            color: h.eloChange >= 0 ? "#22c55e" : "#dc2626" }}>
            {h.eloChange >= 0 ? "+" : ""}{h.eloChange} ELO
          </div>
          <div style={{ fontSize: 10, color: "#555" }}>{h.newElo}</div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DESCENT MAP — keyframes (imperative injection)
// ─────────────────────────────────────────────────────────────
// KEYFRAMES VANISH TRAP: never gate a React-rendered <style> behind a
// module boolean — when the first instance unmounts, React removes its
// <style> and later instances render frozen. Inject once into
// document.head, keyed off the DOM node actually existing, so the
// animations survive any mount/unmount of the map. (Same law HoloFoil
// follows.) → known-traps.md "KEYFRAMES VANISH"
const DEPTHMAP_KEYFRAMES = `
@keyframes dmHereBeat {
  0%,100% { box-shadow: 0 0 16px ${GM.inkGold}, inset 0 0 4px ${GM.inkGoldBright}; }
  50%     { box-shadow: 0 0 26px ${GM.inkGoldBright}, inset 0 0 7px ${GM.inkGoldBright}; }
}
@keyframes dmGatePulse {
  from { box-shadow: 0 0 16px ${GM.inkGold}; }
  to   { box-shadow: 0 0 30px ${GM.inkGoldBright}, 0 0 42px #ff226644; }
}
@keyframes dmPawnBob {
  0%,100% { transform: translate(-50%, -100%); }
  50%     { transform: translate(-50%, -118%); }
}
`;
function ensureDepthMapKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("dm-keyframes")) return;
  const el = document.createElement("style");
  el.id = "dm-keyframes";
  el.textContent = DEPTHMAP_KEYFRAMES;
  document.head.appendChild(el);
}
ensureDepthMapKeyframes(); // at module load, before any paint

// ─────────────────────────────────────────────────────────────
// DESCENT MAP — the level-select trail
// ─────────────────────────────────────────────────────────────
// A serpentine node trail through the Depths. One node per rank
// division; gatekeeper "fortress" nodes at each tier gate. The player's
// equipped companion is the pawn that climbs it. Deeper = further DOWN
// the shaft (you scroll down to descend).
//
// PURE RENDER LAYER: every value it reads (RANKS, GATEKEEPERS,
// depth.elo, getRankInfo, defeatedGatekeepers) already exists. Taps
// route to the SAME handlers as the overview: the current node / a
// forward step → onDescend (rollOpponent); a pending gate →
// onChallengeGate (GatekeeperChallenge). No Firebase, no schema, no
// currency, no rules engine. GM palette only (this is UI chrome).
//
// Sequence order (index 0 → N) is the CLIMB from the surface downward:
//   Escling I..V, [S'uhnker], Beb I..IV, [Z'hefir], Champion I..IV,
//   [T'yrent], Lord I..IV, [V'ael], Escendant I..III, [Rük],
//   Aenarchist I..III, Solrend ∞
// Rendered top→bottom in that same order, so "deeper" is downward and
// the shaft auto-scrolls to center your current node on open.

const DM_STEP_Y   = 96;   // vertical px between node centers
const DM_TOP_PAD  = 44;
const DM_BOT_PAD  = 64;   // room for the Solrend summit at the bottom

// Divisions floor-ELO helper: the ELO at which division d of a rank begins.
function dmDivisionFloor(rank, d) {
  if (rank.maxElo === Infinity) return rank.minElo + (d - 1) * 200;
  const divSpan = (rank.maxElo - rank.minElo + 1) / rank.divisions;
  return rank.minElo + Math.round((d - 1) * divSpan);
}

// Build the ordered descent sequence: gate BEFORE the rank it gates,
// then that rank's division nodes; Solrend is a single summit node.
function dmBuildSequence() {
  const out = [];
  RANKS.forEach((r) => {
    const gate = GATEKEEPERS.find(g => g.grantsEntry === r.id);
    if (gate) out.push({ type: "gate", gate, gateRank: r });
    if (r.id === "solrend") { out.push({ type: "summit", rank: r }); return; }
    for (let d = 1; d <= r.divisions; d++) {
      out.push({ type: "div", rank: r, division: d, floorElo: dmDivisionFloor(r, d) });
    }
  });
  return out;
}

// Serpentine X (percent of shaft width) — a gentle sine weave so the
// trail reads like a classic level-select path rather than a straight line.
function dmXForIndex(i) {
  return 50 + Math.sin(i * 0.9) * 22;
}

function DepthMap({ depth, elo, info, companion, pendingGk,
                    onDescend, onChallengeGate, onOpenGatekeeper }) {
  const [expanded, setExpanded] = useState(false); // false = collapsed (current rank ±1)
  const wrapRef = useRef(null);

  useEffect(() => { ensureDepthMapKeyframes(); }, []);

  const SEQ = dmBuildSequence();

  // Which sequence index is "current"?
  let currentIdx = -1;
  SEQ.forEach((s, i) => {
    if (info.rank.id === "solrend" && s.type === "summit") currentIdx = i;
    else if (s.type === "div" && s.rank.id === info.rank.id && s.division === info.division) currentIdx = i;
  });

  // ── COLLAPSE ──────────────────────────────────────────────
  // When collapsed, only show the current rank, the rank above (deeper,
  // toward the summit), and the rank below (shallower). Ranks outside
  // that window collapse into a single "…" marker so you don't scroll
  // for days just to reach where you left off. Expanded = full journey.
  const rankOrder = RANKS.map(r => r.id);
  const curRankPos = rankOrder.indexOf(info.rank.id);
  const rankVisible = (rankId) => {
    if (expanded) return true;
    const pos = rankOrder.indexOf(rankId);
    return Math.abs(pos - curRankPos) <= 1;
  };

  // Walk the sequence, substituting a single collapse-marker for each
  // contiguous run of hidden nodes.
  const rows = [];
  let hiddenRun = null; // { count, fromRankId, toRankId }
  SEQ.forEach((s, i) => {
    const rankId = s.type === "gate" ? s.gateRank.id : s.rank.id;
    const visible = rankVisible(rankId);
    if (visible) {
      if (hiddenRun) { rows.push({ type: "collapse", ...hiddenRun }); hiddenRun = null; }
      rows.push({ type: "node", seq: s, seqIndex: i });
    } else {
      if (!hiddenRun) hiddenRun = { count: 0, fromRankId: rankId, toRankId: rankId };
      hiddenRun.count++; hiddenRun.toRankId = rankId;
    }
  });
  if (hiddenRun) rows.push({ type: "collapse", ...hiddenRun });

  // Vertical geometry — each visible row gets a slot; collapse markers
  // are shorter than full node slots.
  const ROW_H = { node: DM_STEP_Y, collapse: 52 };
  let cursorY = DM_TOP_PAD;
  const placed = rows.map((row) => {
    const h = ROW_H[row.type];
    const centerY = cursorY + h / 2;
    cursorY += h;
    return { ...row, y: centerY };
  });
  const shaftHeight = cursorY + DM_BOT_PAD;

  // SVG connector path through all placed NODE rows (skip collapse gaps,
  // but bridge across them with a dashed segment).
  const nodePts = placed
    .filter(r => r.type === "node")
    .map(r => ({ x: dmXForIndex(r.seqIndex), y: r.y, idx: r.seqIndex }));
  let pathD = "";
  nodePts.forEach((p, k) => { pathD += (k === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`); });

  // Split point for lit-vs-fog: gradient offset at the current node's y.
  const curRow = placed.find(r => r.type === "node" && r.seqIndex === currentIdx);
  const litOffset = curRow ? (curRow.y / shaftHeight) : 0.5;

  // Auto-center current node on first open (and whenever expand toggles).
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const target = placed.find(r => r.type === "node" && r.seqIndex === currentIdx);
    if (!target) return;
    const t = requestAnimationFrame(() => {
      wrap.scrollTop = target.y - wrap.clientHeight / 2;
    });
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* Expand / collapse toggle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, flexShrink: 0 }}>
        <button onClick={() => setExpanded(v => !v)}
          style={{
            padding: "6px 16px", borderRadius: 20,
            background: `${GM.inkGold}10`, border: `1px solid ${GM.inkBrown}66`,
            color: GM.vellumDim, fontFamily: GM.font, fontSize: 10,
            letterSpacing: 2, cursor: "pointer", fontWeight: 900,
          }}>
          {expanded ? "◆ Collapse to Current" : "◆ View Full Descent"}
        </button>
      </div>

      {/* The scrolling shaft */}
      <div ref={wrapRef} style={{
        flex: 1, position: "relative", overflowY: "auto", overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        maskImage: "linear-gradient(180deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
        WebkitMaskImage: "linear-gradient(180deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)",
      }}>
        <div style={{ position: "relative", width: "100%", height: shaftHeight }}>

          {/* connector path */}
          <svg viewBox={`0 0 100 ${shaftHeight}`} preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}>
            <defs>
              <linearGradient id="dmLit" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={GM.inkGold} />
                <stop offset={Math.max(0, litOffset - 0.01)} stopColor={GM.inkGold} />
                <stop offset={Math.min(1, litOffset + 0.01)} stopColor={GM.pageBorder} />
                <stop offset="1" stopColor={GM.pageWarm} />
              </linearGradient>
            </defs>
            <path d={pathD} fill="none" stroke="#2a2010" strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round" />
            <path d={pathD} fill="none" stroke="url(#dmLit)" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray="2 3" opacity="0.75" />
          </svg>

          {/* rows */}
          {placed.map((row, ri) => {
            if (row.type === "collapse") {
              const fromR = RANKS.find(r => r.id === row.fromRankId);
              const toR   = RANKS.find(r => r.id === row.toRankId);
              const span = fromR && toR && fromR.id !== toR.id
                ? `${fromR.name} – ${toR.name}`
                : (fromR?.name || "");
              return (
                <button key={`c${ri}`} onClick={() => setExpanded(true)}
                  style={{
                    position: "absolute", left: "50%", top: row.y,
                    transform: "translate(-50%, -50%)", zIndex: 3,
                    background: "transparent", border: "none", cursor: "pointer",
                    display: "flex", flexDirection: "column", alignItems: "center",
                    fontFamily: GM.font,
                  }}>
                  <div style={{ fontSize: 16, color: GM.inkGoldDim, letterSpacing: 3, lineHeight: 1 }}>⋮</div>
                  <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 1, marginTop: 2 }}>
                    {row.count} stops · {span}
                  </div>
                </button>
              );
            }

            const s = row.seq;
            const x = dmXForIndex(row.seqIndex);

            // ── SUMMIT (Solrend) ──
            if (s.type === "summit") {
              const isHere = row.seqIndex === currentIdx;
              const reached = elo >= 2800;
              return (
                <div key={`s${ri}`} style={{
                  position: "absolute", left: `${x}%`, top: row.y,
                  transform: "translate(-50%, -50%)", zIndex: 3,
                  width: 160, textAlign: "center", opacity: reached ? 1 : 0.5,
                }}>
                  <div style={{
                    fontSize: 26, color: SD_SOLREND,
                    textShadow: `0 0 20px ${SD_SOLREND}`,
                    animation: isHere ? "dmHereBeat 2.4s ease-in-out infinite" : "none",
                  }}>✦</div>
                  <div style={{ fontSize: 11, fontWeight: 900, color: SD_SOLREND, letterSpacing: 3, marginTop: 2 }}>
                    {isHere ? `SOLREND ${info.division}` : "SOLREND ∞"}
                  </div>
                  <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 2, marginTop: 2, fontStyle: "italic" }}>
                    no ceiling · only deeper
                  </div>
                </div>
              );
            }

            // ── GATE (fortress node) ──
            if (s.type === "gate") {
              const gk = s.gate;
              const defeated = (depth.defeatedGatekeepers || []).includes(gk.id);
              const reached  = elo >= gk.requiredElo;
              const nextRank = RANKS.find(r => r.id === gk.grantsEntry);
              const pending  = reached && !defeated && (nextRank ? nextRank.minElo > elo : false);
              // color/state
              const gStroke = defeated ? "#22c55e"
                : (pending || reached) ? GM.inkGoldBright
                : GM.pageBorder;
              const gColor = defeated ? "#22c55e"
                : (pending || reached) ? GM.inkGoldBright
                : "#4a3d20";
              const clickable = defeated || pending || reached;
              return (
                <button key={`g${ri}`}
                  onClick={() => clickable && onChallengeGate(gk, defeated)}
                  style={{
                    position: "absolute", left: `${x}%`, top: row.y,
                    transform: "translate(-50%, -50%)", zIndex: 3,
                    background: "transparent", border: "none",
                    cursor: clickable ? "pointer" : "default",
                    display: "flex", flexDirection: "column", alignItems: "center",
                    width: 120, fontFamily: GM.font,
                    opacity: (defeated || pending || reached) ? 1 : 0.55,
                  }}>
                  {!defeated && !pending && (
                    <span style={{ fontSize: 11, color: GM.inkGoldDim, marginBottom: -2 }}>⛓</span>
                  )}
                  <div style={{
                    width: 56, height: 56, borderRadius: 8,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, fontWeight: 900,
                    background: "linear-gradient(160deg, #2a1e0a, #14100a)",
                    border: `2px solid ${gStroke}`, color: gColor,
                    boxShadow: defeated ? `0 0 14px #22c55e44`
                      : pending ? `0 0 22px ${GM.inkGold}` : "none",
                    animation: pending ? "dmGatePulse 1.3s ease-in-out infinite alternate" : "none",
                    position: "relative",
                  }}>
                    {defeated ? "✓" : gk.avatarIcon}
                  </div>
                  <div style={{ fontSize: 9, fontWeight: 900, marginTop: 6, letterSpacing: 1,
                    color: defeated ? "#22c55e" : (pending || reached) ? GM.inkGold : "#4a3d20" }}>
                    {gk.name}
                  </div>
                  <div style={{ fontSize: 7, color: GM.vellumFaint, fontStyle: "italic", marginTop: 1,
                    maxWidth: 116, lineHeight: 1.3 }}>
                    {defeated ? "defeated" : reached ? gk.title : `${gk.requiredElo} ELO`}
                  </div>
                </button>
              );
            }

            // ── DIVISION node ──
            const isCurrent = row.seqIndex === currentIdx;
            const nextFloor = s.floorElo + (
              s.rank.maxElo === Infinity
                ? 200
                : Math.round((s.rank.maxElo - s.rank.minElo + 1) / s.rank.divisions)
            );
            const cleared = !isCurrent && elo >= nextFloor;
            const locked  = !isCurrent && elo < s.floorElo;
            const state = isCurrent ? "current" : cleared ? "cleared" : "locked";

            const disc =
              state === "current"
                ? { bg: "radial-gradient(circle at 50% 35%, #3a2c10, #16130b)", border: `2px solid ${GM.inkGoldBright}`, color: s.rank.color, glyph: "◆", anim: "dmHereBeat 2.4s ease-in-out infinite" }
                : state === "cleared"
                ? { bg: "#16130b", border: `1.5px solid ${GM.inkGoldDim}`, color: GM.inkGold, glyph: "✓", anim: "none" }
                : { bg: "#100d07", border: `1.5px solid ${GM.pageBorder}`, color: "#4a3d20", glyph: "◆", anim: "none" };

            return (
              <div key={`d${ri}`} style={{
                position: "absolute", left: `${x}%`, top: row.y,
                transform: "translate(-50%, -50%)", zIndex: 3,
                display: "flex", flexDirection: "column", alignItems: "center",
                width: 96, opacity: state === "locked" ? 0.5 : 1,
              }}>
                {/* the pawn — your companion — hovers over the current node */}
                {isCurrent && (
                  <div style={{
                    position: "absolute", left: "50%", top: 0,
                    transform: "translate(-50%, -100%)",
                    zIndex: 5, pointerEvents: "none",
                    animation: "dmPawnBob 2.4s ease-in-out infinite",
                    filter: "drop-shadow(0 3px 3px rgba(0,0,0,0.8))",
                  }}>
                    {companion.companionId ? (
                      <CompanionSprite companionId={companion.companionId}
                        size={46} variant="standard" radiantTier={companion.radiantTier} />
                    ) : (
                      <div style={{ fontSize: 22, color: s.rank.color }}>☗</div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { if (isCurrent) onDescend(); }}
                  aria-label={isCurrent ? "Descend — next opponent" : `${s.rank.name} ${toRoman(s.division)}`}
                  style={{
                    width: 42, height: 42, borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 15, fontWeight: 900,
                    background: disc.bg, border: disc.border, color: disc.color,
                    cursor: isCurrent ? "pointer" : "default",
                    fontFamily: GM.font,
                    boxShadow: state === "current" ? `0 0 16px ${GM.inkGold}` : "none",
                    animation: disc.anim,
                  }}>
                  {disc.glyph}
                </button>
                <div style={{ fontSize: 8, fontWeight: 900, letterSpacing: 1, marginTop: 5,
                  color: isCurrent ? GM.inkGoldBright : state === "cleared" ? GM.vellumFaint : "#4a3d20",
                  textShadow: isCurrent ? `0 0 8px ${GM.inkGold}` : "none", whiteSpace: "nowrap" }}>
                  {s.rank.name} {toRoman(s.division)}
                </div>
                {/* live sub-progress under the current node */}
                {isCurrent && (
                  <div style={{ width: 64, height: 3, background: "#16130b", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ width: `${Math.round(info.progress * 100)}%`, height: "100%",
                      background: GM.inkGoldBright, boxShadow: `0 0 6px ${GM.inkGold}` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Descend action + gatekeeper alert live BELOW the shaft */}
      <div style={{ flexShrink: 0, paddingTop: 10 }}>
        {pendingGk && (
          <button onClick={() => onOpenGatekeeper(pendingGk)}
            style={{
              width: "100%", padding: "10px", borderRadius: 8, marginBottom: 8,
              background: `${GM.inkGold}12`, border: `1px solid ${GM.inkGold}`,
              color: GM.inkGoldBright, fontFamily: GM.font, fontSize: 11,
              fontWeight: 900, cursor: "pointer", letterSpacing: 1,
              animation: "dmGatePulse 1.4s ease-in-out infinite alternate",
            }}>
            ⚔ {pendingGk.name} blocks {pendingGk.grantsEntry.toUpperCase()} — Challenge
          </button>
        )}
        <button onClick={onDescend}
          style={{
            width: "100%", padding: "14px", borderRadius: 10,
            background: `${SD_SOLREND}15`, border: `1px solid ${SD_SOLREND}`,
            color: SD_SOLREND, fontFamily: GM.font, fontSize: 14, fontWeight: 900,
            cursor: "pointer", letterSpacing: 1, textShadow: `0 0 8px ${SD_SOLREND}`,
          }}>
          ◆ Descend — Next Opponent
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN ENDLESS DEPTHS COMPONENT
// ─────────────────────────────────────────────────────────────
export default function EndlessDepths({ playerName, uid, profile, onClose, onFight }) {
  const [view, setView]           = useState("map");    // map | overview | opponent | gatekeeper | history | ladder
  const companion                 = useEquippedCompanion(uid);
  const [depth, setDepth]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [opponent, setOpponent]   = useState(null);
  const [pendingGatekeeper, setPendingGatekeeper] = useState(null);

  // Load depth profile
  useEffect(() => {
    loadDepthProfile(uid).then(d => { setDepth(d); setLoading(false); });
  }, [uid]);

  if (loading || !depth) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.95)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#ff2266", fontFamily: GM.font, fontSize: 14 }}>
        ◆ Descending...
      </div>
    );
  }

  const elo            = depth.elo || 0;
  const { rank }       = getRankInfo(elo);
  const accentColor    = "#ff2266";

  // Find pending gatekeeper (reached the ELO threshold but haven't beaten them)
  const pendingGk = GATEKEEPERS.find(gk =>
    elo >= gk.requiredElo &&
    !(depth.defeatedGatekeepers || []).includes(gk.id) &&
    RANKS.find(r => r.id === gk.grantsEntry)?.minElo > elo
  );

  // Current opponent difficulty based on rank
  const currentDifficulty = rank.difficulty;
  const winElo = calcEloChange(elo, currentDifficulty, "win");

  // Generate or reroll opponent
  function rollOpponent() {
    const opp = generateOpponent(elo, currentDifficulty);
    setOpponent(opp);
    setView("opponent");
  }

  function handleFight(fighterOpponent) {
    if (!onFight) return;
    // ── DEPTHS BRAIN handoff ──
    // The launch chain (Plaza onFight → App.handleEnterNPC →
    // buildNPCPlayerData) never learns the opponent's ELO or
    // temperament — park the brain in NPC.js for buildNPCPlayerData to
    // consume (one-shot; see setNextNpcBrain). Gatekeepers carry
    // authored values; procedural opponents derived theirs at roll time.
    setNextNpcBrain({
      skill: (typeof fighterOpponent?.npcSkill === "number")
        ? fighterOpponent.npcSkill
        : skillFromElo(elo),
      temperament: fighterOpponent?.temperament || null,
    });
    onFight(fighterOpponent, depth);
  }

  // ── Map / Overview toggle (shared header control) ──────────
  const ModeToggle = () => (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 14 }}>
      {[
        { id: "map", label: "◆ Map" },
        { id: "overview", label: "◆ Overview" },
      ].map(t => {
        const on = view === t.id;
        return (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{
              padding: "6px 18px", borderRadius: 20,
              background: on ? `${accentColor}18` : "transparent",
              border: `1px solid ${on ? accentColor : GM.inkBrown + "55"}`,
              color: on ? accentColor : GM.vellumFaint,
              fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 2,
              cursor: "pointer", textShadow: on ? `0 0 8px ${accentColor}` : "none",
            }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );

  // ── MAP VIEW (landing) — the descent trail ─────────────────
  if (view === "map") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", height: "min(90vh, 860px)",
          display: "flex", flexDirection: "column",
          background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.page})`,
          border: `1px solid ${accentColor}44`, borderRadius: 4,
          padding: "18px 18px 16px",
          boxShadow: `0 0 60px ${accentColor}11, inset 0 0 40px rgba(0,0,0,0.4)` }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 12, flexShrink: 0 }}>
            <div style={{ fontSize: 9, color: accentColor, letterSpacing: 4, marginBottom: 3 }}>
              ◆ THE INNER EMPERIUM ◆
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fff" }}>
              Endless Depths
            </div>
            <div style={{ fontSize: 10, letterSpacing: 1, marginTop: 3, color: rank.color }}>
              {getRankInfo(elo).label} · {elo} ELO
            </div>
          </div>

          <div style={{ flexShrink: 0 }}><ModeToggle /></div>

          {/* The descent map fills the rest */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <DepthMap
              depth={depth}
              elo={elo}
              info={getRankInfo(elo)}
              companion={companion}
              pendingGk={pendingGk}
              onDescend={rollOpponent}
              onChallengeGate={(gk, defeated) => {
                if (defeated) return; // already cleared — nothing to fight
                setPendingGatekeeper(gk);
                setView("gatekeeper");
              }}
              onOpenGatekeeper={(gk) => { setPendingGatekeeper(gk); setView("gatekeeper"); }}
            />
          </div>

          {/* Footer: history / ladder / leave */}
          <div style={{ flexShrink: 0, marginTop: 10 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button onClick={() => setView("history")}
                style={{ flex: 1, padding: "9px", borderRadius: 8,
                  border: `1px solid ${GM.inkBrown}55`, background: "transparent",
                  color: GM.vellumFaint, fontSize: 11, cursor: "pointer", fontFamily: GM.font }}>
                ◆ History
              </button>
              <button onClick={() => setView("ladder")}
                style={{ flex: 1, padding: "9px", borderRadius: 8,
                  border: `1px solid ${GM.inkBrown}55`, background: "transparent",
                  color: GM.vellumFaint, fontSize: 11, cursor: "pointer", fontFamily: GM.font }}>
                ◆ Rank Ladder
              </button>
            </div>
            <button onClick={onClose}
              style={{ width: "100%", padding: "9px", borderRadius: 8,
                border: `1px solid ${GM.inkBrown}44`, background: "transparent",
                color: GM.vellumFaint, fontSize: 11, cursor: "pointer", fontFamily: GM.font }}>
              ← Leave the Depths
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── OVERVIEW VIEW — rank card + rewards (the classic home) ──
  if (view === "overview") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", maxHeight: "90vh", overflowY: "auto",
          background: `linear-gradient(145deg, ${GM.pageMid}, ${GM.page})`,
          border: `1px solid ${accentColor}44`, borderRadius: 4,
          padding: "24px 20px",
          boxShadow: `0 0 60px ${accentColor}11, inset 0 0 40px rgba(0,0,0,0.4)` }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: accentColor, letterSpacing: 4, marginBottom: 4 }}>
              ◆ THE INNER EMPERIUM ◆
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", marginBottom: 4 }}>
              Endless Depths
            </div>
            <div style={{ fontSize: 10, color: "#444", fontStyle: "italic" }}>
              The grind never ends. Neither do the Depths.
            </div>
          </div>

          <ModeToggle />

          {/* Rank card */}
          <RankCard elo={elo} depth={depth} />

          {/* Depth Rewards */}
          <DepthRewardsSection depth={depth} />

          {/* Gatekeeper alert */}
          {pendingGk && (
            <div style={{ background: `${GM.inkGold}10`, border: `1px solid ${GM.inkGold}`,
              borderRadius: 2, padding: "12px 16px", marginBottom: 16,
              textAlign: "center" }}>
              <div style={{ fontSize: 11, color: GM.inkGold, fontWeight: 900, marginBottom: 4 }}>
                ⚔ Gatekeeper Awaits
              </div>
              <div style={{ fontSize: 11, color: "#666" }}>
                {pendingGk.name} blocks your path to {pendingGk.grantsEntry.toUpperCase()}.
              </div>
              <button onClick={() => { setPendingGatekeeper(pendingGk); setView("gatekeeper"); }}
                style={{ marginTop: 10, padding: "8px 20px", borderRadius: 8,
                  background: `${GM.inkGold}15`, border: `1px solid ${GM.inkGold}`,
                  color: GM.inkGoldBright, fontSize: 12, cursor: "pointer",
                  fontFamily: GM.font, fontWeight: 900 }}>
                ⚔ Face Them
              </button>
            </div>
          )}

          {/* Action buttons */}
          <button onClick={rollOpponent}
            style={{ width: "100%", padding: "14px", borderRadius: 10,
              background: `${accentColor}15`, border: `1px solid ${accentColor}`,
              color: accentColor, fontFamily: GM.font, fontSize: 14,
              fontWeight: 900, cursor: "pointer", marginBottom: 10,
              textShadow: `0 0 8px ${accentColor}` }}>
            ◆ Descend
          </button>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <button onClick={() => setView("history")}
              style={{ flex: 1, padding: "10px", borderRadius: 8,
                border: `1px solid ${GM.inkBrown}55`, background: "transparent",
                color: GM.vellumFaint, fontSize: 12, cursor: "pointer",
                fontFamily: GM.font }}>
              ◆ History
            </button>
            <button onClick={() => setView("ladder")}
              style={{ flex: 1, padding: "10px", borderRadius: 8,
                border: `1px solid ${GM.inkBrown}55`, background: "transparent",
                color: GM.vellumFaint, fontSize: 12, cursor: "pointer",
                fontFamily: GM.font }}>
              ◆ Rank Ladder
            </button>
          </div>

          <button onClick={onClose}
            style={{ width: "100%", padding: "10px", borderRadius: 8,
              border: `1px solid ${GM.inkBrown}44`, background: "transparent",
              color: GM.vellumFaint, fontSize: 12, cursor: "pointer", fontFamily: GM.font }}>
            ← Leave the Depths
          </button>
        </div>
      </div>
    );
  }

  // ── OPPONENT VIEW ──────────────────────────────────────────
  if (view === "opponent" && opponent) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", maxHeight: "90vh", overflowY: "auto",
          background: "linear-gradient(145deg, #0d0005, #1a000a)",
          border: `1px solid ${accentColor}33`, borderRadius: 20,
          padding: "24px 20px" }}>
          <OpponentPreview
            opponent={opponent}
            eloChange={winElo}
            onFight={() => handleFight(opponent)}
            onReroll={rollOpponent}
            onBack={() => setView("map")}
          />
        </div>
      </div>
    );
  }

  // ── GATEKEEPER VIEW ────────────────────────────────────────
  if (view === "gatekeeper" && pendingGatekeeper) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", maxHeight: "90vh", overflowY: "auto",
          background: "linear-gradient(145deg, #0d0005, #1a000a)",
          border: `1px solid ${GM.inkGold}33`, borderRadius: 4,
          padding: "24px 20px" }}>
          <GatekeeperChallenge
            gatekeeper={pendingGatekeeper}
            onFight={() => handleFight({ ...pendingGatekeeper, isGatekeeper: true })}
            onBack={() => setView("map")}
          />
        </div>
      </div>
    );
  }

  // ── HISTORY VIEW ───────────────────────────────────────────
  if (view === "history") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", maxHeight: "90vh", overflowY: "auto",
          background: "linear-gradient(145deg, #0d0005, #1a000a)",
          border: `1px solid ${accentColor}33`, borderRadius: 20,
          padding: "24px 20px" }}>
          <div style={{ fontSize: 9, color: accentColor, letterSpacing: 4,
            marginBottom: 16, textAlign: "center" }}>◆ BATTLE HISTORY</div>
          <DepthHistory history={depth.history} />
          <button onClick={() => setView("map")}
            style={{ width: "100%", marginTop: 16, padding: "10px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.08)", background: "transparent",
              color: "#555", fontSize: 12, cursor: "pointer" }}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // ── RANK LADDER VIEW ───────────────────────────────────────
  if (view === "ladder") {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(5,3,0,0.97)", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: GM.font }}>
        <div style={{ width: "min(92vw, 420px)", maxHeight: "90vh", overflowY: "auto",
          background: "linear-gradient(145deg, #0d0005, #1a000a)",
          border: `1px solid ${accentColor}33`, borderRadius: 20,
          padding: "24px 20px" }}>

          <div style={{ fontSize: 9, color: accentColor, letterSpacing: 4,
            marginBottom: 16, textAlign: "center" }}>◆ RANK LADDER</div>

          {RANKS.map((r, i) => {
            const isCurrent = getRankInfo(elo).rank.id === r.id;
            const isUnlocked = elo >= r.minElo;
            const gk = GATEKEEPERS.find(g => g.grantsEntry === r.id);
            const gkDefeated = gk ? (depth.defeatedGatekeepers || []).includes(gk.id) : true;

            return (
              <div key={r.id}>
                {gk && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8,
                    padding: "6px 12px", margin: "4px 0",
                    background: gkDefeated ? "rgba(34,197,94,0.05)" : `${GM.inkGold}08`,
                    border: `1px solid ${gkDefeated ? "#22c55e33" : GM.inkGold + "33"}`,
                    borderRadius: 8 }}>
                    <span style={{ fontSize: 12 }}>⚔</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 900,
                        color: gkDefeated ? "#22c55e" : GM.inkGold }}>
                        {gk.name}
                      </div>
                      <div style={{ fontSize: 9, color: "#444" }}>{gk.title}</div>
                    </div>
                    <div style={{ fontSize: 9, color: gkDefeated ? "#22c55e" : "#555" }}>
                      {gkDefeated ? "✓ DEFEATED" : `${gk.requiredElo} ELO`}
                    </div>
                  </div>
                )}
                <div style={{
                  padding: "12px 16px", borderRadius: 10, marginBottom: 4,
                  background: isCurrent ? `${r.color}15` : "rgba(255,255,255,0.02)",
                  border: `1px solid ${isCurrent ? r.color + "66" : "rgba(255,255,255,0.06)"}`,
                  opacity: isUnlocked ? 1 : 0.4,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: r.color,
                        fontFamily: GM.font }}>
                        {isCurrent ? "◆ " : ""}{r.name}
                        {r.divisions > 0 ? ` I–${toRoman(r.divisions)}` : " ∞"}
                      </div>
                      <div style={{ fontSize: 9, color: "#444", marginTop: 2 }}>
                        {r.maxElo === Infinity ? `${r.minElo}+ ELO` : `${r.minElo}–${r.maxElo} ELO`}
                        {" · "}{r.difficulty}
                      </div>
                    </div>
                    {isUnlocked && <div style={{ fontSize: 12, color: "#22c55e" }}>✓</div>}
                    {!isUnlocked && <div style={{ fontSize: 10, color: "#333" }}>🔒</div>}
                  </div>
                </div>
              </div>
            );
          })}

          <button onClick={() => setView("map")}
            style={{ width: "100%", marginTop: 8, padding: "10px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.08)", background: "transparent",
              color: "#555", fontSize: 12, cursor: "pointer" }}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return null;
}